import { GUEST_QUOTE_LINK_PATH_PREFIX } from '@repo/api-contracts';
import { createBrowserRouter, type RouteObject } from 'react-router';
// FSD: 계층 배럴(@/pages) 금지 — 슬라이스별 public API 로 직접 접근한다.
import { AboutPage } from '@/pages/about';
import { ComingSoonPage } from '@/pages/coming-soon';
import { ConfirmEmailChangePage } from '@/pages/confirm-email-change';
import { CustomizePage } from '@/pages/customize';
import { ExhibitionPage } from '@/pages/exhibition';
import { GuestQuoteLinkPage } from '@/pages/guest-quote-link';
import { GuestQuoteRequestPage } from '@/pages/guest-quote-request';
import { HomePage } from '@/pages/home';
import { MyDesignsPage } from '@/pages/my-designs';
import { MyHomePage } from '@/pages/my-home';
import { MyOrderDetailPage } from '@/pages/my-order-detail';
import { MyOrdersPage } from '@/pages/my-orders';
import { MyProfilePage } from '@/pages/my-profile';
import { MyQuoteDetailPage } from '@/pages/my-quote-detail';
import { MyQuotesPage } from '@/pages/my-quotes';
import { MyReviewsPage } from '@/pages/my-reviews';
import { MyWithdrawPage } from '@/pages/my-withdraw';
import { NotFoundPage } from '@/pages/not-found';
import { OauthCallbackPage } from '@/pages/oauth-callback';
import { OnboardingPage } from '@/pages/onboarding';
import { OrderDepositPage } from '@/pages/order-deposit';
import { OrderInquiryWritePage } from '@/pages/order-inquiry-write';
import { OrderPlacePage } from '@/pages/order-place';
import { PaymentCheckoutPage } from '@/pages/payment-checkout';
import { PaymentFailPage } from '@/pages/payment-fail';
import { PaymentSuccessPage } from '@/pages/payment-success';
import { PlainQuoteRequestPage } from '@/pages/plain-quote-request';
import { PortfolioPage } from '@/pages/portfolio';
import { PortfolioDetailPage } from '@/pages/portfolio-detail';
import { QuoteGuidePage } from '@/pages/quote-guide';
import { QuoteRequestPage } from '@/pages/quote-request';
import { ResetPasswordPage } from '@/pages/reset-password';
import { ReviewEditPage } from '@/pages/review-edit';
import { ReviewWritePage } from '@/pages/review-write';
import { SignInPage } from '@/pages/sign-in';
import { SignUpPage } from '@/pages/sign-up';
import { StorePage } from '@/pages/store';
import { StoreDetailPage } from '@/pages/store-detail';
import { VerifyEmailPage } from '@/pages/verify-email';
import { WithdrawnPage } from '@/pages/withdrawn';
import {
  ABOUT_PATH,
  EXHIBITION_PATH,
  GNB_ITEMS,
  GUEST_QUOTE_REQUEST_PATH,
  HOME_PATH,
  MY_DESIGNS_PATH,
  MY_ORDERS_PATH,
  MY_PAGE_PATH,
  MY_PROFILE_PATH,
  MY_QUOTES_PATH,
  MY_REVIEWS_PATH,
  MY_WITHDRAW_PATH,
  ORDER_DEPOSIT_PATH,
  ORDER_PLACE_PATH,
  PAYMENT_CHECKOUT_PATH,
  PAYMENT_FAIL_PATH,
  PAYMENT_SUCCESS_PATH,
  PLAIN_QUOTE_REQUEST_PATH,
  PORTFOLIO_PATH,
  QUOTE_REQUEST_PATH,
  STORE_CUSTOMIZE_ROUTE_PATTERN,
  STORE_PATH,
  WITHDRAWN_PATH,
} from '@/shared/config';
import { AppShell } from './app-shell';
import { loadLegalDraftPage } from './legal-draft-loader';
import { legalRoutes } from './legal-routes';
import { RequireActive } from './require-active';
import { RequireAuth } from './require-auth';

/** 화면 표 — 모두 아래 앱 셸 한 겹 아래에 놓인다. */
const appRoutes: RouteObject[] = [
  // 공개 라우트 — 메일 링크(verify-email · reset-password)는 비로그인에서도 열린다.
  { path: '/sign-in', element: <SignInPage /> },
  { path: '/sign-up', element: <SignUpPage /> },
  { path: '/verify-email', element: <VerifyEmailPage /> },
  { path: '/confirm-email-change', element: <ConfirmEmailChangePage /> },
  { path: '/reset-password', element: <ResetPasswordPage /> },
  { path: '/oauth/callback', element: <OauthCallbackPage /> },
  // 탈퇴 완료 — 세션이 이미 비워진 뒤에 열리므로 공개 라우트다.
  { path: WITHDRAWN_PATH, element: <WithdrawnPage /> },

  // HOME 랜딩 — 공개 라우트.
  { path: HOME_PATH, element: <HomePage /> },

  // 스토어 — 공개 라우트(비로그인 탐색 허용).
  { path: STORE_PATH, element: <StorePage /> },
  { path: `${STORE_PATH}/:productId`, element: <StoreDetailPage /> },
  // 커스터마이징(워프 Phase 7) — 공개 라우트. 비로그인은 편집·미리보기까지, 저장은 로그인(U2).
  { path: STORE_CUSTOMIZE_ROUTE_PATTERN, element: <CustomizePage /> },

  // 기획전 — 공개 라우트(비로그인 탐색 허용).
  { path: EXHIBITION_PATH, element: <ExhibitionPage /> },
  { path: PORTFOLIO_PATH, element: <PortfolioPage /> },
  { path: `${PORTFOLIO_PATH}/:id`, element: <PortfolioDetailPage /> },
  // 회사 소개 — 공개 라우트.
  { path: ABOUT_PATH, element: <AboutPage /> },
  // 견적문의 안내 — 공개 라우트. 진행 과정·저장한 디자인·바로가기·고객센터만 안내하고, 디자인 요청 폼은 아래 보호 라우트
  // (/request/:designId)다. 편집영역 없는 상품은 상세에서 바로 요청한다(2026-10-30 일반 견적 설계) — 아래 일반 견적 요청 라우트.
  { path: QUOTE_REQUEST_PATH, element: <QuoteGuidePage /> },
  // 비회원 견적 요청(비회원 견적 설계 G12) — 공개 라우트. 에디터 임시 작업으로 연락처·동의만 받아 접수한다.
  // 정적 조각(guest)이라 react-router 가 보호 라우트의 /request/:designId 보다 먼저 고른다.
  { path: GUEST_QUOTE_REQUEST_PATH, element: <GuestQuoteRequestPage /> },
  // 일반 견적 요청(2026-10-30 일반 견적 설계) — 공개 라우트. 편집영역 없는 상품을 디자인 없이 요청한다(로그인이면 회원, 아니면 비회원 폼).
  // 정적 접두(product)라 react-router 가 보호 라우트의 /request/:designId 보다 먼저 고른다.
  { path: `${PLAIN_QUOTE_REQUEST_PATH}/:productId`, element: <PlainQuoteRequestPage /> },
  // 견적 링크(비회원 견적 요청, 결정 G8·G9) — 공개 라우트. 서명 링크로 상태별 내용을 보고, [주문하기]로 가입·연결을 시작한다.
  {
    path: `${GUEST_QUOTE_LINK_PATH_PREFIX}/:quoteRequestId/:signature`,
    element: <GuestQuoteLinkPage />,
  },

  // GNB 준비중 페이지 — 메뉴 구성은 shared/config/gnb.ts 가 단일 원천이다.
  // 실제 페이지가 생기면 여기서 빼고 위 공개 라우트로 승격한다.
  ...GNB_ITEMS.filter(
    (item) =>
      item.path !== HOME_PATH &&
      item.path !== STORE_PATH &&
      item.path !== EXHIBITION_PATH &&
      item.path !== PORTFOLIO_PATH &&
      item.path !== ABOUT_PATH &&
      item.path !== QUOTE_REQUEST_PATH,
  ).map((item) => ({
    path: item.path,
    element: <ComingSoonPage title={item.label} />,
  })),
  // 약관·개인정보처리방침 — 공개 라우트. 약관 페이지 스위치(VITE_LEGAL_PAGES_ENABLED, 빌드 시점)를 끈 빌드는 준비 중 페이지,
  // 켠 빌드는 처음 열 때 약관 페이지를 불러온다(legal-routes.tsx·legal-draft-loader.ts).
  ...legalRoutes(loadLegalDraftPage),

  // 없는 주소 — 위 어느 라우트에도 맞지 않으면 404. (react-router 는 가장 구체적인 경로를 먼저 고르므로 순서와 무관하게 마지막 순위다)
  { path: '*', element: <NotFoundPage /> },

  // 보호 라우트 — 세션 만료 시 가드가 로그인으로 보낸다.
  {
    element: <RequireAuth />,
    children: [
      { path: `${ORDER_DEPOSIT_PATH}/:orderId`, element: <OrderDepositPage /> },
      { path: '/onboarding', element: <OnboardingPage /> },
      // 결제는 ACTIVE 전용 — 서버의 activeProcedure 가 막고, 가드는 로그인만 본다(결제 설계 §11).
      { path: PAYMENT_CHECKOUT_PATH, element: <PaymentCheckoutPage /> },
      { path: PAYMENT_SUCCESS_PATH, element: <PaymentSuccessPage /> },
      { path: PAYMENT_FAIL_PATH, element: <PaymentFailPage /> },
      // 결제 3종·견적 요청·주문하기는 로그인만 보고, 마이페이지는 아래 RequireActive 가 ACTIVE 만 들인다.
      { path: `${QUOTE_REQUEST_PATH}/:designId`, element: <QuoteRequestPage /> },
      // 탈퇴는 PENDING 계정도 할 수 있어야 해서(서버 @AllowStatus) RequireActive 밖에 두고 가드는 로그인만 본다.
      { path: MY_WITHDRAW_PATH, element: <MyWithdrawPage /> },
      // 마이페이지 — ACTIVE 계정 전용(홈·내 견적·내 디자인·회원정보·내 주문·후기). 인증이 덜 된 계정은 온보딩으로 보낸다.
      // 서버(activeProcedure)가 ACTIVE 와 본인 데이터를 다시 막는다. 경로 헬퍼는 값을 인코딩하므로 라우트 패턴은 상수로 직접 잇는다.
      {
        element: <RequireActive />,
        children: [
          { path: MY_PAGE_PATH, element: <MyHomePage /> },
          { path: MY_DESIGNS_PATH, element: <MyDesignsPage /> },
          { path: MY_PROFILE_PATH, element: <MyProfilePage /> },
          { path: MY_QUOTES_PATH, element: <MyQuotesPage /> },
          { path: `${MY_QUOTES_PATH}/:id`, element: <MyQuoteDetailPage /> },
          { path: MY_ORDERS_PATH, element: <MyOrdersPage /> },
          { path: `${MY_ORDERS_PATH}/:orderId`, element: <MyOrderDetailPage /> },
          { path: `${MY_ORDERS_PATH}/:orderId/review`, element: <ReviewWritePage /> },
          { path: `${MY_ORDERS_PATH}/:orderId/inquiry`, element: <OrderInquiryWritePage /> },
          { path: MY_REVIEWS_PATH, element: <MyReviewsPage /> },
          { path: `${MY_REVIEWS_PATH}/:reviewId/edit`, element: <ReviewEditPage /> },
        ],
      },
      // 주문하기 — 페이지가 gate(VITE_ORDER_PLACEMENT_ENABLED, 기본 닫힘)를 보고 닫혀 있으면 준비 중 안내만 낸다.
      { path: `${ORDER_PLACE_PATH}/:quoteRequestId`, element: <OrderPlacePage /> },
    ],
  },
];

// 앱 셸 — 모든 화면 위에 한 번 놓인다. 비회원 견적 연결 훅(ACTIVE 가 되면 연결)과 연결 안내를 띄운다(결정 G11).
export const router = createBrowserRouter([{ element: <AppShell />, children: appRoutes }]);
